import { memo } from 'react';
import { css } from '@emotion/react';
import { EdgeProps, getBezierPath, EdgeLabelRenderer } from 'reactflow';
import { typography } from '@uniquegood/realworld-studio-design';

import type { QuestEdgeData, QuestEdgeType } from '../types';

const EDGE_COLORS: Record<QuestEdgeType, string> = {
  reveal: '#22c55e',
  unlock: '#3b82f6',
  accomplish: '#f59e0b',
  lock: '#ef4444'
};

const EDGE_DASHED: Record<QuestEdgeType, boolean> = {
  reveal: false,
  unlock: false,
  accomplish: false,
  lock: true
};

function QuestDependencyEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  style
}: EdgeProps<QuestEdgeData>) {
  const edgeType = data?.edgeType ?? 'reveal';
  const edgeColor = EDGE_COLORS[edgeType];
  const isDashed = EDGE_DASHED[edgeType];

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition
  });

  return (
    <>
      <defs>
        <marker
          id={`qdep-arrow-${id}`}
          markerWidth="10"
          markerHeight="7"
          refX="9"
          refY="3.5"
          orient="auto"
        >
          <path d="M0,0 L10,3.5 L0,7 Z" fill={edgeColor} />
        </marker>
      </defs>
      <path
        id={id}
        d={edgePath}
        markerEnd={`url(#qdep-arrow-${id})`}
        style={{
          ...style,
          stroke: edgeColor,
          strokeWidth: 1.5,
          strokeDasharray: isDashed ? '6 3' : undefined,
          fill: 'none'
        }}
      />
      <EdgeLabelRenderer>
        <div
          css={LabelStyle}
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            color: edgeColor,
            borderColor: edgeColor
          }}
        >
          {data?.label ?? ''}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

export default memo(QuestDependencyEdge);

const LabelStyle = css`
  position: absolute;
  pointer-events: none;
  font-size: 10px;
  font-weight: ${typography.weight.bold};
  letter-spacing: 0.04em;
  background-color: #fff;
  padding: 1px 5px;
  border-radius: 3px;
  border: 1px solid;
`;
